import { useState, useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { AuthContext } from "../../context/AuthContext";
import { useIsMobile } from "../../hooks/useIsMobile";
import { useNavigate, useLocation } from "react-router-dom";
import "./Authentication.css";
import { Helmet } from "react-helmet";
import SecondButton from "../../components/Elements/SecondButton/SecondButton";
import AuthenticationService from "../../services/AuthenticationService";
import { validateLoginInput, parseLoginError } from "../../utils/loginErrorHandler";
import { validateEmailInput, parseForgotPasswordError } from "../../utils/forgotPasswordErrorHandler";
import { validateResetPasswordInput, parseResetPasswordError } from "../../utils/resetPasswordErrorHandler";
import { validateRegisterInput, parseRegisterError } from "../../utils/registerErrorHandler";
import { validateVerificationCodeInput, parseVerificationCodeError } from "../../utils/verificationCodeErrorHandler";
import envelopeIcon from "../../assets/icons/auth/forms/envelope.png";
import lockIcon from "../../assets/icons/auth/forms/lock.png";
import userIcon from "../../assets/icons/auth/forms/user.png";
import hashtag from "../../assets/icons/auth/forms/lock-hashtag.png";
import eye from "../../assets/icons/auth/forms/eye.png";
import eyeOff from "../../assets/icons/auth/forms/eye-crossed.png";
import AuthLoginForm from "../../components/Authentication/AuthLoginForm";
import AuthRegisterFlow from "../../components/Authentication/AuthRegisterFlow";
import AuthForgotPasswordFlow from "../../components/Authentication/AuthForgotPasswordFlow";
import LoginTwoFactorModal from "../../components/Authentication/LoginTwoFactorModal";
import { trackAnalyticsEvent } from "../../analytics/analytics";
import brand from "../../config/brand";

// Main authentication component handling all auth flows (login/register/forgot-password)
const Login = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || "en";
  // Router hooks for navigation
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile(770);
  const { setToken, setFirebaseToken } = useContext(AuthContext);

  // Core UI state
  const [showPassword, setShowPassword] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  // Flow-specific state
  const [forgotPasswordStage, setForgotPasswordStage] = useState("email");
  const [verificationStage, setVerificationStage] = useState("form");
  const [resetStatus, setResetStatus] = useState("sending");

  // Form input state
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [resetCode, setResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");

  // UI feedback state
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // 2FA state
  const [isTwoFactorModalOpen, setIsTwoFactorModalOpen] = useState(false);
  const [twoFactorMethod, setTwoFactorMethod] = useState(null);
  const [pendingEmail, setPendingEmail] = useState("");
  const [pendingRedirectUrl, setPendingRedirectUrl] = useState(`/${lang}/account`);

  // Reset all form state to initial values
  const resetFormState = () => {
    setUsername("");
    setEmail("");
    setPassword("");
    setResetCode("");
    setNewPassword("");
    setError(null);
  };

  // Load saved credentials on mount
  useEffect(() => {
    const savedEmail = localStorage.getItem("Email");
    const savedPassword = localStorage.getItem("Password");
    if (savedEmail) setEmail(savedEmail);
    if (savedPassword) setPassword(savedPassword);

    if (location.state?.isRegistering !== undefined) {
      setIsRegistering(location.state.isRegistering);
    }
  }, [location.state]);

  const persistRememberMe = (loginEmail, loginPassword) => {
    if (rememberMe) {
      localStorage.setItem("Email", loginEmail);
      localStorage.setItem("Password", loginPassword);
    } else {
      localStorage.removeItem("Email");
      localStorage.removeItem("Password");
    }
  };

  const finalizeLogin = (response, loginEmail, loginPassword) => {
    setError(null);
    setToken(response.access_token);
    setFirebaseToken(response.firebase_token);

    localStorage.setItem("token", response.access_token);
    localStorage.setItem("firebaseToken", response.firebase_token);

    persistRememberMe(loginEmail, loginPassword);
    trackAnalyticsEvent.login("email");
    navigate(response.redirect_url || `/${lang}/account`);
  };

  // Handle login submission
  const handleLogin = async () => {
    setError("");

    const validationError = validateLoginInput(email, password);
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      const redirectUrl = `/${lang}/account`;
      const response = await AuthenticationService.loginUser(email, password, redirectUrl);

      // Najpierw sprawdzamy 2FA
      if (response.two_factor_method) {
        setPendingEmail(response.email || email);
        setPendingRedirectUrl(response.redirect_url || redirectUrl);
        setTwoFactorMethod(response.two_factor_method);
        setIsTwoFactorModalOpen(true);
        setError(null);
        return;
      }

      // Potem normalny login z tokenem
      if (response.access_token) {
        finalizeLogin(response, email, password);
        return;
      }

      setError(t("errors.unexpectedLoginResponse", "Unexpected login response."));
    } catch (err) {
      const message = parseLoginError(err.response);
      setError(message);
    }
  };

  // Handle registration verification email
  const handleSendVerificationEmail = async () => {
    setError("");
    const validationError = validateRegisterInput(username, email, password);
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      await AuthenticationService.sendVerificationCode(email, lang);
      setVerificationStage("code");
    } catch (err) {
      const message = parseRegisterError(err);
      setError(message);
    }
  };

  // Handle registration code verification
  const handleVerifyRegistrationCode = async () => {
    setError("");
    const validationError = validateVerificationCodeInput(verificationCode);
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      await AuthenticationService.verifyEmailCode(email, verificationCode);
      await AuthenticationService.registerUser(email, password, username);
      setIsRegistering(false);
      setSuccessMessage("Your email has been successfully verified.");
      trackAnalyticsEvent.signUp("email");
      resetFormState();
    } catch (err) {
      const message = parseVerificationCodeError(err);
      setError(message);
    }
  };

  // Handle forgot password email
  const handleSendResetEmail = async () => {
    setError("");
    const validationError = validateEmailInput(email);
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      await AuthenticationService.sendResetCode(email);
      setForgotPasswordStage("reset");
      setResetStatus("sent");
    } catch (err) {
      const message = parseForgotPasswordError(err);
      setError(message);
      setResetStatus("error");
    }
  };

  // Handle password reset
  const handleResetPassword = async () => {
    setError("");
    const validationError = validateResetPasswordInput(email, resetCode, newPassword);
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      await AuthenticationService.resetPassword(email, resetCode, newPassword);
      setIsForgotPassword(false);
      setForgotPasswordStage("email");
      setResetCode("");
      setNewPassword("");
      setSuccessMessage("Your password has been successfully changed.");
    } catch (err) {
      const message = parseResetPasswordError(err);
      setError(message);
      setSuccessMessage(null);
    }
  };

  // Toggle between login/register modes
  const toggleRegisterMode = () => {
    setIsRegistering(!isRegistering);
    resetFormState();
    setIsForgotPassword(false);
    setForgotPasswordStage("email");
    setVerificationStage("form");
    setError(null);
  };

  return (
    <div className="auth-container">
      <Helmet>
        <title>{brand.name} - {isRegistering ? t("auth.register", "Sign Up") : t("auth.signIn", "Sign In")}</title>
      </Helmet>

      {/* Welcome section with dynamic content */}
      <div className="welcome-section">
        <h1>
          {isRegistering ? t("auth.joinTitle", `Join ${brand.name}!`) : t("auth.welcomeBackTitle", "Welcome Back!")}
        </h1>
        <p className="description">
          {isRegistering
            ? t("auth.joinLead", "Create your account to access exclusive deals and seamless travel services.")
            : t(
                "auth.welcomeBackLead",
                "To keep connected with us, please log in with your personal information. If you're new here, feel free to create an account and join our community.",
              )}
        </p>
        {!isMobile && (
          <SecondButton className="toggle-register-desktop" onClick={toggleRegisterMode}>
            {isRegistering ? t("auth.signIn", "Sign In") : t("auth.register", "Register")}
          </SecondButton>
        )}
      </div>

      {/* Dynamic form rendering based on current auth flow */}
      <div className="sign-in-section">
        {!isRegistering && !isForgotPassword && (
          <AuthLoginForm
            envelopeIcon={envelopeIcon}
            lockIcon={lockIcon}
            eye={eye}
            eyeOff={eyeOff}
            email={email}
            password={password}
            rememberMe={rememberMe}
            showPassword={showPassword}
            error={error}
            isMobile={isMobile}
            onEmailChange={setEmail}
            onPasswordChange={setPassword}
            onRememberChange={setRememberMe}
            onTogglePassword={() => setShowPassword((prev) => !prev)}
            onForgotPassword={() => {
              setIsForgotPassword(true);
              setForgotPasswordStage("email");
              setError(null);
              setEmail("");
            }}
            onLogin={handleLogin}
            onSwitchToRegister={toggleRegisterMode}
            successMessage={successMessage}
            setSuccessMessage={setSuccessMessage}
            onCloseError={() => setError(null)}
          />
        )}

        {isRegistering && (
          <AuthRegisterFlow
            verificationStage={verificationStage}
            userIcon={userIcon}
            envelopeIcon={envelopeIcon}
            lockIcon={lockIcon}
            hashtag={hashtag}
            eye={eye}
            eyeOff={eyeOff}
            username={username}
            email={email}
            password={password}
            verificationCode={verificationCode}
            showPassword={showPassword}
            error={error}
            isMobile={isMobile}
            onUsernameChange={setUsername}
            onEmailChange={setEmail}
            onPasswordChange={setPassword}
            onVerificationCodeChange={setVerificationCode}
            onTogglePassword={() => setShowPassword((prev) => !prev)}
            onSendVerificationEmail={handleSendVerificationEmail}
            onVerifyRegistrationCode={handleVerifyRegistrationCode}
            onSwitchToLogin={toggleRegisterMode}
            onBackToForm={() => {
              setVerificationStage("form");
              setError(null);
            }}
            onCloseError={() => setError(null)}
          />
        )}

        {isForgotPassword && (
          <AuthForgotPasswordFlow
            forgotPasswordStage={forgotPasswordStage}
            resetStatus={resetStatus}
            envelopeIcon={envelopeIcon}
            lockIcon={lockIcon}
            hashtag={hashtag}
            eye={eye}
            eyeOff={eyeOff}
            email={email}
            resetCode={resetCode}
            newPassword={newPassword}
            showPassword={showPassword}
            error={error}
            onEmailChange={setEmail}
            onResetCodeChange={setResetCode}
            onNewPasswordChange={setNewPassword}
            onTogglePassword={() => setShowPassword((prev) => !prev)}
            onSendResetEmail={handleSendResetEmail}
            onResetPassword={handleResetPassword}
            onBackToLogin={() => {
              setIsForgotPassword(false);
              setForgotPasswordStage("email");
              resetFormState();
              setResetStatus("sending");
              setError(null);
            }}
            onCloseError={() => setError(null)}
          />
        )}

        {/* 2FA Modal */}
        <LoginTwoFactorModal
          isOpen={isTwoFactorModalOpen}
          onClose={() => setIsTwoFactorModalOpen(false)}
          method={twoFactorMethod}
          email={pendingEmail}
          redirectUrl={pendingRedirectUrl}
          password={password}
          onSuccess={(response) => finalizeLogin(response, pendingEmail, password)}
        />
      </div>
    </div>
  );
};

export default Login;
